import classNames from 'classnames';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Checkbox, MenuList, MenuListItem, ScrollView, Separator, Window, WindowContent, WindowHeader } from 'react95';
import { head, pick, sum } from 'lodash-es';
import { setTaskListFilters } from '../../actions/taskListActions';
import { ALL_SUBSTAGES } from '../../global/Environment';
import {
  showAllReturnsSelector,
  taskListCurrentTabSelector,
  taskListFiltersSelector,
  yearSelector
} from '../../selectors/taskListSelectors';
import { getStageDisplayName, getStageOptions, isCurrentUserEng } from '../../utils/taskUtils';
import './TaskListSidePanel.scss';
import { useGetTaskStageCountsQuery, useGetTaskSubstageCountsQuery } from '../../api/opsSystemApi';
import { userSelector } from '../../selectors/authSelectors';
import { useFetchQDeckUsersQuery } from '../../api/qdeckApi';
import { TASK_LIST_TABS } from '../../constants';

const TaskListSidePanel = ({ onShowAllReturnsToggle }) => {
  const dispatch = useDispatch();

  const year = useSelector(yearSelector);
  const showAllReturns = useSelector(showAllReturnsSelector);
  const filters = useSelector(taskListFiltersSelector);
  const user = useSelector(userSelector);
  const currentTab = useSelector(taskListCurrentTabSelector);

  const isSubstageView = currentTab === TASK_LIST_TABS.SUBSTAGE_TASKS;

  const { data: qdeckUsers = [], isLoading: areQdeckUsersLoading } = useFetchQDeckUsersQuery();
  const isEng = isCurrentUserEng(user.email, qdeckUsers);

  const stages = getStageOptions(showAllReturns, isEng);
  const { data: substageCounts } = useGetTaskSubstageCountsQuery(
    {
      taxYear: year,
      stages,
      showAllReturns,
      filters
    },
    {
      skip: !isSubstageView || areQdeckUsersLoading
    }
  );
  const { data: stageCounts } = useGetTaskStageCountsQuery(
    {
      taxYear: year,
      showAllReturns,
      filters
    },
    {
      skip: isSubstageView
    }
  );

  const onListItemClick = async (filterVal) => {
    if (isSubstageView) {
      dispatch(setTaskListFilters({ ...filters, substage: head(filterVal) || null }));
    } else {
      dispatch(setTaskListFilters({ ...filters, stages: filterVal }));
    }
  };

  const stageTotal = stageCounts ? sum(Object.values(pick(stageCounts, stages))) : 0;
  const substageTotal = substageCounts ? sum(Object.values(substageCounts)) : 0;
  const isAllStagesSelected = !filters?.stages?.length && !filters.substage;

  return (
    <Window className='task-list-side-panel'>
      <WindowHeader className='task-list-side-panel-header'>
        <span className='task-list-side-panel-header-text'>Tasks by {isSubstageView ? 'Substage' : 'Stage'}</span>
      </WindowHeader>

      <WindowContent className='task-list-side-panel-content'>
        <Checkbox
          name='allReturnsToggle'
          label='Show all returns'
          className={classNames('task-list-side-panel-stages-toggle', {
            checked: showAllReturns
          })}
          checked={showAllReturns}
          onChange={() => onShowAllReturnsToggle(!showAllReturns)}
        />
        <MenuList className='task-list-side-panel-items'>
          {!isSubstageView && (
            <MenuListItem
              primary
              onClick={() => onListItemClick(null)}
              className={classNames({ selected: isAllStagesSelected }, 'task-list-side-panel-total')}>
              <span>All Stages</span>
              <span>{isSubstageView ? substageTotal : stageTotal}</span>
            </MenuListItem>
          )}

          <Separator />

          <ScrollView>
            {isSubstageView
              ? ALL_SUBSTAGES.map((substage) => {
                  const isSelected = filters.substage === substage.value;
                  return (
                    <MenuListItem
                      primary
                      className={classNames({ selected: isSelected })}
                      onClick={() => {
                        onListItemClick([substage.value]);
                      }}
                      key={substage.value}>
                      <span className='task-list-side-panel-stage'>
                        <div>{substage.label}</div>
                      </span>
                      <span className='task-list-side-panel-count'>
                        {substageCounts && substageCounts[substage.value] ? substageCounts[substage.value] : 0}
                      </span>
                    </MenuListItem>
                  );
                })
              : stages.map((stage) => {
                  const isSelected = filters?.stages?.includes(stage);
                  return (
                    <MenuListItem
                      primary
                      className={classNames({ selected: isSelected })}
                      onClick={() => {
                        onListItemClick([stage]);
                      }}
                      key={stage}>
                      <span className='task-list-side-panel-stage'>{getStageDisplayName(stage)}</span>
                      <span className='task-list-side-panel-count'>
                        {stageCounts && stageCounts[stage] ? stageCounts[stage] : 0}
                      </span>
                    </MenuListItem>
                  );
                })}
          </ScrollView>
        </MenuList>
      </WindowContent>
    </Window>
  );
};

export default TaskListSidePanel;
